import { Component, OnInit, inject, signal } from "@angular/core";
import { AbstractControl, AsyncValidatorFn, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Router, ActivatedRoute, RouterLink } from "@angular/router";
import { MessageService, ConfirmationService, PrimeTemplate } from "primeng/api";
import { catchError, map, Observable, of, switchMap, timer } from "rxjs";
import { AbstractFormComponent } from "src/app/common/abstract-edit.component";
import {
  COUNTRY_LIST,
  FISCAL_CODE_VALIDATOR_REGEX,
  PARAM_CLIENTS_PATH,
  STATE_LIST,
  CLIENT_TYPE_LIST,
} from "src/app/constants/constants";
import { BookingsService } from "src/app/services/bookings.service";
import { Clients } from "src/app/models/clients";
import { Category } from "src/app/models/category";
import { Locations } from "src/app/models/locations";
import { ClientsService } from "src/app/services/clients.service";
import { Comuni } from "src/app/models/comuni";
import { Billings } from "src/app/models/billings";
import { BillingsService } from "src/app/services/billings.service";
import { Bookings } from "src/app/models/bookings";
import { TopNavComponent } from "../../top-nav/top-nav.component";
import { Bind } from "primeng/bind";
import { ButtonDirective } from "primeng/button";
import { Card } from "primeng/card";
import { Tabs, TabList, Tab, TabPanels, TabPanel } from "primeng/tabs";
import { Ripple } from "primeng/ripple";
import { SelectButton } from "primeng/selectbutton";
import { InputText } from "primeng/inputtext";
import { AddressSuggestComponent } from "../../components/address-suggest/address-suggest.component";
import { Select } from "primeng/select";
import { AddressMapComponent } from "../../components/address-map/address-map.component";
import { DatePipe } from "@angular/common";
import { InputMask } from "primeng/inputmask";
import { Textarea } from "primeng/textarea";
import { Message } from "primeng/message";
import { Timeline } from "primeng/timeline";
import { ConfirmDialog } from "primeng/confirmdialog";
import { Drawer } from "primeng/drawer";
import { BookingDetailComponent } from "../../components/booking-detail/booking-detail.component";
import { Toast } from "primeng/toast";
import { TranslatePipe } from "@ngx-translate/core";

import { TPipe } from "../../i18n/t.pipe";
import { PhoneInputComponent } from "../../components/phone-input/phone-input.component";
@Component({
    standalone: true,
    selector: "app-clients-form",
    templateUrl: "./clients-form.component.html",
    imports: [PhoneInputComponent, TPipe, TopNavComponent, FormsModule, ReactiveFormsModule, Bind, ButtonDirective, RouterLink, Card, Tabs, TabList, Ripple, Tab, TabPanels, TabPanel, SelectButton, InputText, AddressSuggestComponent, Select, AddressMapComponent, InputMask, Textarea, PrimeTemplate, Message, Timeline, ConfirmDialog, Drawer, BookingDetailComponent, Toast, DatePipe, TranslatePipe]
})
export class ClientsFormComponent
  extends AbstractFormComponent<Clients>
  implements OnInit
{
  protected fb: FormBuilder;
  private bookingsService = inject(BookingsService);
  private billingsService = inject(BillingsService);
  private clientsService = inject(ClientsService);

  pageTitle: string;
  form: FormGroup;
  idField = "id";
  formFields = [
    "id",
    "name",
    "surname",
    "city",
    "zip",
    "address",
    "province",
    "region",
    "country",
    "lat",
    "lng",
    "email",
    "phone",
    "fiscalcode",
    "fiscalnumber",
    "description",
    "category_id",
    "is_active",
    "is_featured",
    "date",
  ];
  item$: Observable<Clients> = of();
  stateOptions = STATE_LIST;
  businessStateOptions = CLIENT_TYPE_LIST;

  readonly createdAt = signal<string | null>(null);

  get is_featured(): boolean {
    const value = this.form?.get("is_featured")?.value;
    return value === "1" || value === 1 || value === true;
  }
  service = this.clientsService;
  client: Clients;
  clients: Clients[] = [];
  readonly bookings = signal<Bookings[]>(undefined);
  categories: Category[] = [];
  category: Category;
  locations: Locations[] = [];
  comuni: Comuni[] = [];
  comuniCategory: Comuni[] = [];
  location: Locations;
  readonly billings = signal<Billings[]>(undefined);
  readonly countries = signal<any[]>(undefined);
  readonly booking = signal<Bookings>(undefined);
  readonly bookedDialog = signal<boolean>(false);

  constructor() {
    const fb = inject(FormBuilder);
    const messageService = inject(MessageService);
    const confirmationService = inject(ConfirmationService);
    const router = inject(Router);
    const route = inject(ActivatedRoute);

    super(
      fb,
      messageService,
      confirmationService,
      router,
      route,
      PARAM_CLIENTS_PATH,
    );

    this.fb = fb;
  }

  emailExistsValidator(
    clientsService: ClientsService,
    getCurrentId: () => string | null,
  ): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const email = (control.value || "").trim();
      if (!email) return of(null);

      const currentId = getCurrentId();

      const paramsRequests = {
        _start: 0,
        _limit: 10,
        is_booked: "0",
        email,
      };

      return timer(300).pipe(
        switchMap(() => clientsService.list(paramsRequests)),
        map((clients: Clients[]) => {
          if (!clients || clients.length === 0) return null;

          const exists = currentId
            ? clients.some((c) => c.id !== currentId)
            : true;

          return exists ? { emailExists: true } : null;
        }),
        catchError(() => of(null)),
      );
    };
  }

  ngOnInit() {
    this.initializeForm();
    this.countries.set(COUNTRY_LIST);

    const id = this.route.snapshot.paramMap.get("id");
    if (id) {
      this.pageTitle = "Modifica Cliente";
      this.loadItem(id);
    } else {
      this.pageTitle = "Aggiungi Cliente";
    }
  }

  hasNoSelectedBookings() {
    return (
      this.bookings().filter((booking) => booking.title).length === 0
    );
  }

  hasNoSelectedBillings() {
    return this.billings().filter((billing) => billing.title).length === 0;
  }

  editRoom(booking: Bookings) {
    this.booking.set({ ...booking });
    this.bookedDialog.set(true);
  }

  initializeForm() {
    this.form = this.fb.group({
      id: [""],
      name: ["", Validators.required],
      surname: ["", Validators.required],
      city: [""],
      zip: [""],
      address: [""],
      province: [""],
      region: [""],
      country: [""],
      lat: [""],
      lng: [""],
      email: [
        "",
        {
          validators: [Validators.email],
          asyncValidators: [
            this.emailExistsValidator(this.clientsService, () => {
              const id = this.route.snapshot.paramMap.get("id");
              return id ? id : null;
            }),
          ],
          updateOn: "blur",
        },
      ],
      phone: [""],
      fiscalcode: new FormControl("", Validators.compose([codFisc])),
      fiscalnumber: new FormControl(""),
      description: [""],
      category_id: [""],
      is_active: ["0"],
      is_featured: ["1"],
      date: [""],
    });
  }

  loadItem(id: string) {
    this.service.getId(id).subscribe(
      (res) => {
        const params = {
          page: 0,
          size: 10,
          category_id: id,
        };
        this.billingsService.list(params).subscribe((pData) => {
          this.billings.set(pData);
        });

        this.createdAt.set(res.created_at ? String(res.created_at).replace(" ", "T") : null);

        this.form.patchValue({
          name: res.name,
          surname: res.surname,
          city: res.city,
          zip: res.zip,
          address: res.address,
          province: res.province,
          region: res.region,
          country: res.country,
          lat: res.lat,
          lng: res.lng,
          email: res.email,
          phone: res.phone,
          fiscalcode: res.fiscalcode,
          fiscalnumber: res.fiscalnumber,
          description: res.description,
          category_id: res.category_id,
          is_active: res.is_active,
          is_featured: res.is_featured,
          date: res.date,
          id: res.id,
        });

        const paramsBookings = {
          page: 0,
          size: 100,
          client_id: res.id,
          category_id: id,
        };
        this.bookingsService.list(paramsBookings).subscribe((pData) => {
          this.bookings.set(pData);
        });
      },
      (error) => (this.error = error),
    );
  }
}

export function codFisc(c: FormControl): { [s: string]: boolean } {
  if (c.value && !c.value.match(FISCAL_CODE_VALIDATOR_REGEX)) {
    return { invalidCF: true };
  }
}
